import { Box } from '@mui/system';
import { Loader } from '@openx/components-core/lib/Loader/Loader';
import { type MetacategoryData, MetacategoryGroup } from '@openx/types/targeting/metacategory';
import { TargetingFields, type TargetingFieldsValues } from '@openx/types/targeting/targeting';
import { Criteria } from '@openx/types/targeting/targetingValuesTypes';
import { noop } from '@openx/utils/noop';
import cloneDeep from 'lodash/cloneDeep';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useTargetingContext } from '../../../../utils/context/TargetingContext';
import { useTargetingChildDisabledState } from '../../../../utils/hooks/useTargetingChildDisabledState';
import { useTargetingDisabledState } from '../../../../utils/hooks/useTargetingDisabledState';
import { DimensionChipLine } from '../../../shared/DimensionChip/DimensionChipLine';
import { EmptyFilterMessage } from '../../../shared/EmptyFilterMessage/EmptyFilterMessage';
import { FiltersTitle } from '../../../shared/FiltersTitle/FiltersTitle';
import {
  TargetingDropdownSection,
  type TargetingDropdownSectionProps,
} from '../../../shared/ReadOnlyTargetingDropdown/TargetingDropdownSection';
import type { TargetingItemsProps } from '../../../types';
import { MetacategoryAllowBlockItem } from './MetacategoryAllowBlockItem';

type ChildBlockingState = {
  isDisabled: boolean;
  hasError: boolean;
  disabledReasonMessage?: string;
  errorReasonMessage?: string;
};

type SectionBlockingStateParams = {
  childState: ChildBlockingState;
  isParentDisabled: boolean;
  hasParentError: boolean;
  parentDisabledReasonMessage: string | undefined;
  parentErrorReasonMessage: string | undefined;
};

const getSectionBlockingState = ({
  childState,
  isParentDisabled,
  hasParentError,
  parentDisabledReasonMessage,
  parentErrorReasonMessage,
}: SectionBlockingStateParams) => ({
  isDisabled: childState.isDisabled || childState.hasError || isParentDisabled || hasParentError,
  disabledReasonMessage: childState.disabledReasonMessage ?? parentDisabledReasonMessage,
  errorReasonMessage: childState.errorReasonMessage ?? parentErrorReasonMessage,
});

export const MetacategoryFormPreviewItems = ({
  targetingParams,
  readonly = true,
  onOpenDrawerClick,
  onRemove,
  onRemoveCategories,
  onRemoveKeywords,
  disabledReasonMessage,
  field = TargetingFields.METACATEGORY,
}: TargetingItemsProps<MetacategoryData> &
  Omit<TargetingDropdownSectionProps, 'isDisabled' | 'disabledReasonMessage'> & {
    onRemoveCategories?: () => void;
    onRemoveKeywords?: () => void;
    field?: TargetingFieldsValues;
  }) => {
  const {
    isDisabled: isMetacategoryDisabled,
    disabledReasonMessage: metacategoryDisabledReasonMessage,
    hasError: hasMetacategoryError,
    errorReasonMessage: metacategoryErrorReasonMessage,
  } = useTargetingDisabledState(field);

  const { getDisabledStateForOption } = useTargetingChildDisabledState(field, disabledReasonMessage);

  const { t } = useTranslation();
  const { useMetacategoriesFetch } = useTargetingContext();

  const { refetchMetacategories, metacategories, areMetacategoriesLoading } = useMetacategoriesFetch();
  const clonedTargetingParams = useMemo(() => cloneDeep(targetingParams), [targetingParams]);
  const { includes, excludes, inter_dimension_operator, keywords } = clonedTargetingParams;

  const keywordsIncludes = keywords.includes;
  const keywordsExcludes = keywords.excludes;

  const itemsSet = useMemo(() => new Set([...(includes?.val || []), ...excludes]), [includes, excludes]);
  const itemsSetKeywords = useMemo(
    () => new Set([...keywordsIncludes, ...keywordsExcludes]),
    [keywordsExcludes, keywordsIncludes]
  );

  useEffect(() => {
    refetchMetacategories();
  }, [refetchMetacategories]);

  if (areMetacategoriesLoading) {
    return <Loader />;
  }

  if (!itemsSet.size && !itemsSetKeywords.size && !readonly) {
    return <EmptyFilterMessage text={t('No filters applied.')} />;
  }

  const bothCategoriesEnabled = !!(itemsSet.size && itemsSetKeywords.size);

  const parentDisabledReasonMessage = metacategoryDisabledReasonMessage ?? disabledReasonMessage;
  const parentErrorReasonMessage = metacategoryErrorReasonMessage;

  const categoriesSectionState = getSectionBlockingState({
    childState: getDisabledStateForOption(MetacategoryGroup.ADDITIONAL_OPENX_CATEGORIES),
    isParentDisabled: isMetacategoryDisabled,
    hasParentError: hasMetacategoryError,
    parentDisabledReasonMessage,
    parentErrorReasonMessage,
  });

  const keywordsSectionState = getSectionBlockingState({
    childState: getDisabledStateForOption(MetacategoryGroup.KEYWORDS),
    isParentDisabled: isMetacategoryDisabled,
    hasParentError: hasMetacategoryError,
    parentDisabledReasonMessage,
    parentErrorReasonMessage,
  });

  return (
    <Box data-test="sub-section">
      <TargetingDropdownSection
        name={t('OpenX Categories')}
        hideButtons={!readonly}
        isEmpty={!itemsSet.size}
        onOpenDrawerClick={() => onOpenDrawerClick?.(MetacategoryGroup.ADDITIONAL_OPENX_CATEGORIES)}
        onRemove={onRemoveCategories ?? onRemove}
        isDisabled={categoriesSectionState.isDisabled}
        disabledReasonMessage={categoriesSectionState.disabledReasonMessage}
        hasOptionsSelected={!!itemsSet.size}
      >
        {!!itemsSet.size && (
          <>
            <FiltersTitle
              title={t('OpenX Categories [ {count} ]', { count: itemsSet.size })}
              readonly={readonly}
              disabledReasonMessage={categoriesSectionState.errorReasonMessage}
            />

            <MetacategoryAllowBlockItem
              options={metacategories}
              handleChange={noop}
              includes={includes?.val || []}
              op={includes?.op}
              excludes={excludes}
              readonly={readonly}
              dataTest="categories"
            />
          </>
        )}
      </TargetingDropdownSection>

      {readonly ? (
        <DimensionChipLine showLabel={bothCategoriesEnabled} switchState={inter_dimension_operator === Criteria.ANY} />
      ) : (
        bothCategoriesEnabled && (
          <DimensionChipLine
            showLabel={bothCategoriesEnabled}
            switchState={inter_dimension_operator === Criteria.ANY}
          />
        )
      )}

      <TargetingDropdownSection
        name={t('Keywords')}
        hideButtons={!readonly}
        isEmpty={!itemsSetKeywords.size}
        onOpenDrawerClick={() => onOpenDrawerClick?.(MetacategoryGroup.KEYWORDS)}
        onRemove={onRemoveKeywords ?? onRemove}
        isDisabled={keywordsSectionState.isDisabled}
        disabledReasonMessage={keywordsSectionState.disabledReasonMessage}
        hasOptionsSelected={!!itemsSetKeywords.size}
      >
        {!!itemsSetKeywords.size && (
          <>
            <FiltersTitle
              title={t('Keywords [ {count} ]', { count: itemsSetKeywords.size })}
              readonly={readonly}
              disabledReasonMessage={keywordsSectionState.errorReasonMessage}
            />

            <MetacategoryAllowBlockItem
              // options not required, just display string from value
              options={undefined}
              handleChange={noop}
              includes={keywordsIncludes}
              excludes={keywordsExcludes}
              readonly={readonly}
              dataTest="keywords"
            />
          </>
        )}
      </TargetingDropdownSection>
    </Box>
  );
};
